'use client'

import ThemeSwitch from '@/components/layout/header/ThemeSwitch'
import SubscribeModalTrigger from '@/components/modals/triggers/SubscribeModalTrigger'
import SearchButton from '@/components/search/SearchButton'
import { Button } from '@/components/ui/Button'
import Container from '@/components/ui/Container'
import { requestHomeReset } from '@/lib/home-filter'
import { MENU_MORE, PRIMARY_NAV, isNavActive } from '@/lib/nav'
import { track } from '@/lib/tracking'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { RiArrowRightSLine, RiUserLine } from 'react-icons/ri'

export default function Header({ userData }) {
  const pathname = usePathname()

  // Mobile-only top bar. The hamburger menu lists every page: the three
  // primary destinations first (the same PRIMARY_NAV the bottom tab bar
  // carries, because a reader who opens a menu expects to find the whole
  // site in it), then every other page (MENU_MORE). Both come from
  // lib/nav.js so the menu can't drift from the tab bar again (2026-09-28: it
  // had its own copy, which left Storylines and Sources out of it).

  // Same nav_click event as the top bar and tab bar, so nav reports can
  // compare the three.
  function navClick(href, label) {
    track('nav_click', { href, label, location: 'header_mobile' })
    if (href === '/') requestHomeReset()
  }

  return (
    <Disclosure
      as="header"
      className="rule-double relative bg-background md:hidden"
    >
      <Container>
        {/* The identity mocks' phone header (2026-10-31): the 34px lockup on
            the left; on the right the outlined Subscribe and a bordered
            44px menu button, 14px above and below. Search moved into the
            menu. */}
        <div className="flex items-center justify-between py-[14px]">
          {/* Logo */}
          <Link
            href="/"
            onClick={requestHomeReset}
            className="transition-opacity hover:opacity-80"
          >
            <Image
              alt="Pano News"
              src="/brand/pano-news-horizontal.svg"
              width={874}
              height={256}
              className="h-[34px] w-auto dark:hidden"
              priority
              unoptimized
            />
            <Image
              alt="Pano News"
              src="/brand/pano-news-horizontal-reversed.svg"
              width={874}
              height={256}
              className="hidden h-[34px] w-auto dark:block"
              priority
              unoptimized
            />
          </Link>

          {/* Search (the phone mock: an icon in the bar, opening a full
              screen sheet), the subscribe CTA, then the menu button. */}
          <div className="flex items-center gap-1.5">
            <SearchButton location="header_mobile_bar" />
            {userData?.type === 'guest' ? (
              <SubscribeModalTrigger size="sm" location="header_mobile" />
            ) : (
              <Button href="/account" variant="outline" size="sm">
                Account
              </Button>
            )}
            <DisclosureButton className="group inline-flex size-11 items-center justify-center rounded-lg border border-foreground text-foreground transition-colors hover:bg-muted focus:outline-none">
              <span className="sr-only">Open main menu</span>
              <Bars3Icon
                aria-hidden="true"
                className="block size-5 group-data-open:hidden"
              />
              <XMarkIcon
                aria-hidden="true"
                className="hidden size-5 group-data-open:block"
              />
            </DisclosureButton>
          </div>
        </div>
      </Container>

      {/* The phone menu (2026-11-01), top to bottom: a search box, the
          three destinations in the headline serif, the rest of the site in
          two columns, the reader's account, a dark mode switch, and the
          legal line. Every link closes the menu as it goes. */}
      <DisclosurePanel className="max-h-[calc(100dvh-64px)] overflow-y-auto border-t border-border md:hidden">
        <Container className="pb-6 pt-4">
          <SearchButton location="header_mobile" as="field" />

          <nav aria-label="Main" className="mt-3">
            {PRIMARY_NAV.map((item) => {
              const active = isNavActive(item.href, pathname)
              return (
                <DisclosureButton
                  key={item.href}
                  as={Link}
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navClick(item.href, item.label)}
                  className={`flex min-h-[52px] items-center justify-between border-b border-border font-serif text-[22px] text-foreground ${
                    active ? 'font-medium' : ''
                  }`}
                >
                  <span
                    className={
                      active ? 'underline underline-offset-[6px]' : undefined
                    }
                  >
                    {item.label}
                  </span>
                  <RiArrowRightSLine
                    className="h-5 w-5 text-muted-foreground"
                    aria-hidden="true"
                  />
                </DisclosureButton>
              )
            })}
          </nav>

          <nav
            aria-label="More"
            className="mt-4 grid grid-cols-2 gap-x-4 border-b border-border pb-3"
          >
            {MENU_MORE.map((item) => (
              <DisclosureButton
                key={item.href}
                as={Link}
                href={item.href}
                aria-current={
                  isNavActive(item.href, pathname) ? 'page' : undefined
                }
                onClick={() => navClick(item.href, item.label)}
                className={`flex min-h-11 items-center font-sans text-[15px] ${
                  isNavActive(item.href, pathname)
                    ? 'font-semibold text-foreground'
                    : 'text-secondary-foreground'
                }`}
              >
                {item.label}
              </DisclosureButton>
            ))}
          </nav>

          <div className="border-b border-border">
            <DisclosureButton
              as={Link}
              href={userData?.type === 'guest' ? '/login' : '/account'}
              className="flex min-h-12 items-center gap-3 font-sans text-[15px] text-foreground"
            >
              <RiUserLine className="h-5 w-5" aria-hidden="true" />
              {userData?.type === 'guest' ? 'Log in' : 'Your account'}
            </DisclosureButton>
            <ThemeSwitch />
          </div>

          <p className="mt-4 font-sans text-[11px] text-muted-foreground">
            &copy; {new Date().getFullYear()} Pano News ·{' '}
            <Link href="/privacy-policy" className="hover:text-foreground">
              Privacy
            </Link>{' '}
            ·{' '}
            <Link href="/terms-of-service" className="hover:text-foreground">
              Terms
            </Link>
          </p>
        </Container>
      </DisclosurePanel>
    </Disclosure>
  )
}
